import { useEffect, useMemo, useRef, useState } from "react";

/**
 * SignupPage
 *
 * Email and password, then a code by e-mail — the phone number is asked for
 * later, and only if the customer chooses SMS as a second factor.
 *
 * Props:
 *   onSubmit({ email, password, turnstileToken }) — called when the form is submitted
 *   onSocialLogin(provider: 'google' | 'github')
 *   turnstileSiteKey — renders the human check when the deployment has one
 */
export default function SignupPage({ onSubmit, onSocialLogin, turnstileSiteKey = "" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 12) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    return score; // 0-4
  }, [password]);

  const strengthColor = ["var(--fill-danger)", "var(--fill-danger)", "var(--fill-warning)", "var(--fill-success)", "var(--fill-success)"][strength];
  const strengthHint =
    password.length === 0
      ? "Needs 1 number and 1 special character."
      : strength < 4
      ? "Weak — add length, a number, a symbol."
      : "Strong password.";

  const passwordsMatch = confirm.length === 0 || confirm === password;

  function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) return;
    onSubmit?.({ email, password, turnstileToken });
  }

  return (
    <div style={{ maxWidth: 400, margin: "0 auto", background: "var(--surface-1)", borderRadius: "var(--radius-lg)", padding: "2rem" }}>
      <p style={{ fontSize: "var(--text-xl)", fontWeight: 500, margin: "0 0 4px" }}>Create your account</p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 20px" }}>
        Start monitoring iCal feeds in minutes.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button type="button" className="ical-btn" style={{ flex: 1 }} onClick={() => onSocialLogin?.("google")}>
          Google
        </button>
        <button type="button" className="ical-btn" style={{ flex: 1 }} onClick={() => onSocialLogin?.("github")}>
          GitHub
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>or with email</span>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      </div>

      <form onSubmit={handleSubmit}>
        <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Email</label>
        <input
          className="ical-input"
          type="email"
          required
          placeholder="name@company.com"
          style={{ width: "100%", marginBottom: 12 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Password</label>
        <input
          className="ical-input"
          type="password"
          required
          minLength={12}
          placeholder="Min. 12 characters"
          style={{ width: "100%" }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div style={{ height: 4, borderRadius: 2, background: "var(--border)", margin: "6px 0 2px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${(strength / 4) * 100}%`, background: strengthColor, transition: "width .2s" }} />
        </div>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "0 0 12px" }}>{strengthHint}</p>

        <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Confirm password</label>
        <input
          className="ical-input"
          type="password"
          required
          placeholder="Repeat password"
          style={{ width: "100%", marginBottom: 4, borderColor: passwordsMatch ? undefined : "var(--fill-danger)" }}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {!passwordsMatch && (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-danger)", margin: "0 0 12px" }}>Passwords don't match.</p>
        )}

        <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />

        <button type="submit" className="ical-btn ical-btn-primary" style={{ width: "100%", marginTop: 8 }}>
          Create account
        </button>
      </form>

      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textAlign: "center", margin: "14px 0 0" }}>
        We'll e-mail you a 6-digit code to verify your address.
      </p>
    </div>
  );
}

/**
 * TurnstileWidget renders Cloudflare's human check.
 *
 * The widget is loaded explicitly rather than by injecting a form-bound script,
 * because the token has to travel in the signup body — and because a widget that
 * fails to load must be visible rather than silently absent. When it renders
 * nothing (no site key configured) the deployment has no check and the server
 * says so at startup.
 */
function TurnstileWidget({ siteKey, onToken }) {
  const container = useRef(null);
  // The callback is held in a ref so a re-render of the parent — every keystroke
  // in the password field is one — does not tear down and rebuild the widget.
  const callback = useRef(onToken);
  callback.current = onToken;

  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return undefined;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !window.turnstile || !container.current) return;
        window.turnstile.render(container.current, {
          sitekey: siteKey,
          callback: (token) => callback.current?.(token),
          "expired-callback": () => callback.current?.(""),
          "error-callback": () => callback.current?.(""),
        });
      })
      .catch(() => {
        // The server refuses a signup with no token, with a message that says
        // what to do; this only makes the cause visible on the page.
        if (!cancelled && container.current) {
          container.current.textContent = "The human check could not be loaded. Reload the page to try again.";
        }
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={container} data-testid="turnstile" style={{ marginBottom: 12 }} />;
}

/** loadTurnstile loads the widget script once per page. */
let turnstilePromise = null;
function loadTurnstile() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve();
  if (turnstilePromise) return turnstilePromise;

  turnstilePromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      turnstilePromise = null;
      reject(new Error("turnstile failed to load"));
    };
    document.head.appendChild(script);
  });
  return turnstilePromise;
}
